/**
 * MCP response formatting helpers for PlaywrightClient.
 *
 * @module playwright-client/response-format
 * @see docs/api/services/browser-session.md
 */

import type { CapturedDownload, CapturedToast } from './internal-types.js';
import { formatFileSize } from './download-utils.js';

/** Number of snapshots a captured toast persists before expiring. */
export const TOAST_SNAPSHOT_TTL = 5;

interface FormatMcpResponseInput {
  result: string;
  snapshot?: string;
  code?: string;
  title?: string;
  pageUrl?: string;
  capturedToasts: CapturedToast[];
  capturedDownloads: CapturedDownload[];
  lastReportedDownloadIndex: number;
}

interface FormatMcpResponseOutput {
  text: string;
  nextCapturedToasts: CapturedToast[];
  nextLastReportedDownloadIndex: number;
}

/**
 * Format response in MCP-compatible markdown format.
 * Matches MCP output structure while keeping mutation decisions explicit.
 */
export function formatMcpResponse(input: FormatMcpResponseInput): FormatMcpResponseOutput {
  const lines: string[] = [];

  // Surface any toast notifications captured (with TTL tracking)
  if (input.capturedToasts.length > 0) {
    lines.push('### Toast Notifications');
    for (const toast of input.capturedToasts) {
      const remaining = toast.snapshotsRemaining ?? 1;
      const placementHint = toast.placement
        ? ` [visible at (${toast.placement.x}, ${toast.placement.y}), ${toast.placement.width}×${toast.placement.height}px]`
        : '';
      lines.push(
        `- [${toast.role}] ${toast.text}${placementHint} (expires in ${remaining} snapshot${remaining !== 1 ? 's' : ''})`
      );
    }
    lines.push('');
  }

  // Surface new downloads captured since last report
  const newDownloads = input.capturedDownloads.slice(input.lastReportedDownloadIndex);
  const completedNew = newDownloads.filter((d) => d.completed);
  let nextLastReportedDownloadIndex = input.lastReportedDownloadIndex;
  if (completedNew.length > 0) {
    lines.push('### Downloads');
    for (const dl of completedNew) {
      const size =
        dl.sizeBytes !== null && dl.sizeBytes !== undefined
          ? ` (${formatFileSize(dl.sizeBytes)})`
          : '';
      lines.push(
        `- [${dl.id}] ${dl.suggestedFilename}${size} — use browser_read_download to view content`
      );
    }
    lines.push('');
    nextLastReportedDownloadIndex = input.capturedDownloads.length;
  }

  if (input.result) {
    lines.push('### Result');
    lines.push(input.result);
    lines.push('');
  }

  if (input.code) {
    lines.push('### Ran Playwright code');
    lines.push('```js');
    lines.push(input.code);
    lines.push('```');
    lines.push('');
  }

  if (input.snapshot && input.pageUrl) {
    lines.push('### Page state');
    lines.push(`- Page URL: ${input.pageUrl}`);
    if (input.title) {
      lines.push(`- Page Title: ${input.title}`);
    }
    lines.push('- Page Snapshot:');
    lines.push('```yaml');
    lines.push(input.snapshot);
    lines.push('```');
  }

  // Decrement TTL and keep toasts that still have remaining snapshots
  const nextCapturedToasts = input.capturedToasts
    .map((t) => ({ ...t, snapshotsRemaining: (t.snapshotsRemaining ?? 1) - 1 }))
    .filter((t) => t.snapshotsRemaining > 0);

  return {
    text: lines.join('\n') || '### Result\nOK',
    nextCapturedToasts,
    nextLastReportedDownloadIndex,
  };
}
